import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCompact } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingDown, TrendingUp, Scale, FileSpreadsheet, ShieldAlert } from "lucide-react";

const CHART_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4"];
const tooltipStyle = { backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, color: "#1f2937" };

const KpiCard = ({ icon: Icon, label, value, sub, subColor = "text-slate-500", to, testId, delay }) => (
  <div
    className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 hover:ring-1 hover:ring-[#B01E24]/20 transition-shadow fade-up"
    style={{ animationDelay: `${delay * 60}ms` }}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <span className="text-[10.5px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      <Icon size={15} className="text-[#B01E24]" strokeWidth={1.8} />
    </div>
    <div className="mt-2.5 text-2xl font-bold text-slate-900 tracking-tight font-mono">{value}</div>
    <div className={`mt-1 text-xs ${subColor}`}>{sub}</div>
    {to && (
      <Link to={to} data-testid={`${testId}-view-report`} className="inline-block mt-2 text-xs font-medium text-[#B01E24] hover:text-[#9A171D]">
        View report →
      </Link>
    )}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  if (err)
    return <div className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-4 max-w-lg" data-testid="dashboard-no-access">{err}</div>;
  if (!data)
    return <div className="text-slate-500 text-sm animate-pulse">Loading dashboard…</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Financial Overview</h1>
        <p className="text-sm text-slate-500 mt-1">Live position extracted from SAP S/4HANA Private Cloud · Dee Piping Systems · As of {data.as_of}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard icon={TrendingDown} label="Total Payables (AP · Credit)" value={formatCompact(k.total_payables)}
          sub={`${formatCompact(k.ap_overdue)} overdue · ${k.ap_invoices} invoices`} subColor="text-red-600"
          to="/reports/ap-ageing" testId="kpi-total-payables" delay={0} />
        <KpiCard icon={TrendingUp} label="Total Receivables (AR · Debit)" value={formatCompact(k.total_receivables)}
          sub={`${formatCompact(k.ar_overdue)} overdue · ${k.ar_invoices} invoices`} subColor="text-amber-600"
          to="/reports/ar-ageing" testId="kpi-total-receivables" delay={1} />
        <KpiCard icon={Scale} label="Net Position (AR − AP)" value={formatCompact(k.net_position)}
          sub={k.net_position >= 0 ? "Net receivable" : "Net payable"}
          testId="kpi-net-position" delay={2} />
        <KpiCard icon={FileSpreadsheet} label="ITC Available (GSTR-2)" value={formatCompact(k.itc_available)}
          sub={`${k.purchase_invoices} purchase invoices · ${k.gst_match_rate}% 2B match`} subColor="text-cyan-700"
          to="/reports/gstr2" testId="kpi-itc-available" delay={3} />
      </div>

      {data.msme && (
        <div className="rounded-lg border border-amber-300 bg-amber-50/60 shadow-sm p-5 flex flex-wrap items-center gap-6 fade-up" data-testid="dashboard-msme-block">
          <div className="flex items-start gap-3 flex-1 min-w-[260px]">
            <div className="rounded-full bg-amber-100 border border-amber-300 p-2 text-amber-700"><ShieldAlert size={18} /></div>
            <div>
              <div className="text-[10.5px] font-semibold uppercase tracking-widest text-amber-800">MSME Suppliers — Outstanding</div>
              <div className="mt-1 text-2xl font-bold text-slate-900 font-mono">{formatCompact(data.msme.outstanding)}</div>
              <div className="text-xs text-slate-500 mt-0.5">{data.msme.invoices} open invoices to registered MSME / Udyam vendors</div>
              <Link to="/reports/ap-ageing" data-testid="dashboard-msme-link" className="inline-block mt-1.5 text-xs font-medium text-[#B01E24] hover:text-[#9A171D]">View MSME payables in AP Ageing →</Link>
            </div>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 min-w-[240px]" data-testid="dashboard-msme-45-card">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-red-700">Beyond 45-day MSME limit</div>
            <div className="mt-1 text-xl font-bold text-red-700 font-mono">{formatCompact(data.msme.beyond_45)}</div>
            <div className="text-[11px] text-red-600 mt-0.5">{data.msme.beyond_45_count} invoices · est. interest ~{formatCompact(data.msme.est_interest)}</div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 fade-up" data-testid="ageing-chart-card">
        <h3 className="text-sm font-semibold text-slate-800 mb-4">Ageing Distribution — AP vs AR</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.ageing_chart} barGap={4}>
            <XAxis dataKey="bucket" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => formatCompact(v)} width={72} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCompact(v)} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="AP" fill="#1D6FA5" radius={[3, 3, 0, 0]} />
            <Bar dataKey="AR" fill="#F59E0B" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 fade-up" data-testid="top-customers-card">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Top Customers by Outstanding</h3>
          <div className="space-y-3">
            {data.top_customers.map((c, i) => (
              <div key={c.name} className="flex items-center gap-3">
                <span className="text-[11px] font-mono text-slate-400 w-4">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-700 truncate">{c.name}</span>
                    <span className="font-mono text-slate-500">{formatCompact(c.value)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-[#B01E24] rounded-full" style={{ width: `${(c.value / data.top_customers[0].value) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 fade-up" data-testid="grir-chart-card">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">GR/IR Status (GRN vs MIRO)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.grir_chart} layout="vertical" margin={{ left: 30 }}>
              <XAxis type="number" stroke="#94a3b8" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} width={130} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
              <Bar dataKey="value" radius={[0, 3, 3, 0]}>
                {data.grir_chart.map((e, i) => (
                  <Cell key={i} fill={e.name === "Matched" ? "#10B981" : e.name === "GRN Not Invoiced" ? "#EF4444" : "#F59E0B"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <Link to="/reports/grir" className="text-xs font-medium text-[#B01E24] hover:text-[#9A171D]" data-testid="grir-view-report">View report →</Link>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5 fade-up" data-testid="inventory-chart-card">
          <h3 className="text-sm font-semibold text-slate-800 mb-4">Inventory Ageing Split</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.inventory_chart} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                {data.inventory_chart.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="none" />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCompact(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
          <Link to="/reports/stock-ageing" className="text-xs font-medium text-[#B01E24] hover:text-[#9A171D]" data-testid="inventory-view-report">View report →</Link>
        </div>
      </div>
    </div>
  );
}
